// ===========================================
// Student Progress Report Page
// ===========================================
// Comprehensive student progress report with filters, summary, charts, table, and export

import { useState } from 'react';
import { useSchoolWeek } from '../../../hooks/useSchoolWeek';
import {
  Box,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { School as SchoolIcon } from '@mui/icons-material';

import type { StudentProgressFilters } from '../../../api/reports.api';
import {
  useStudentProgressReport,
  useExportStudentProgressReport,
} from '../../../hooks/useReports';
import PageHeader from '../../../components/common/PageHeader';
import StudentProgressFiltersComponent from '../../../components/reports/StudentProgressFilters';
import StudentProgressSummaryCards from '../../../components/reports/StudentProgressSummaryCards';
import ProgressByLevelChart from '../../../components/reports/ProgressByLevelChart';
import PieceCompletionChart from '../../../components/reports/PieceCompletionChart';
import StudentStreakChart from '../../../components/reports/StudentStreakChart';
import StudentProgressTable from '../../../components/reports/StudentProgressTable';
import ExportButtons from '../../../components/reports/ExportButtons';

// ===========================================
// COMPONENT
// ===========================================

export default function StudentProgressReportPage() {
  // The page opens on the SCHOOL's current week (Monday–Sunday) rather than a
  // "select filters" card — an empty card reads as "no data". The heading says
  // which week, leading with the term week when one applies.
  const { week, describe } = useSchoolWeek();
  const [filters, setFilters] = useState<StudentProgressFilters>(() => ({ ...week }));
  const [appliedFilters, setAppliedFilters] = useState<StudentProgressFilters>(() => ({ ...week }));
  // Remounts the filter card on Reset so its date pickers show the week again.
  const [filtersKey, setFiltersKey] = useState(0);

  // Fetch report data (only when filters are applied)
  const { data: reportData, isLoading, isError, refetch } = useStudentProgressReport(appliedFilters);
  const exportMutation = useExportStudentProgressReport();

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleResetFilters = () => {
    setFilters({ ...week });
    setAppliedFilters({ ...week });
    setFiltersKey((k) => k + 1);
  };

  const handleExport = (format: 'csv' | 'pdf') => {
    exportMutation.mutate({ format, filters: appliedFilters });
  };

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Student Progress Report"
        subtitle={describe(appliedFilters) ?? "Track student learning progress, attendance, and achievements"}
        icon={<SchoolIcon />}
        action={
          reportData && (
            <ExportButtons
              onExport={handleExport}
              loading={exportMutation.isPending}
              disabled={!reportData}
            />
          )
        }
      />

      {/* Filters */}
      <StudentProgressFiltersComponent
        key={filtersKey}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State — never an empty table: an error is not "no data" */}
      {!isLoading && isError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The student progress report could not be loaded. Nothing below reflects this period.
        </Alert>
      )}

      {/* Report Content */}
      {!isLoading && reportData && (
        <>
          {/* Summary Cards */}
          <StudentProgressSummaryCards summary={reportData.summary} />

          {/* Charts Grid Row 1 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Progress by Level Chart - 6 columns */}
            <Grid item xs={12} md={6}>
              <ProgressByLevelChart data={reportData.byLevel} />
            </Grid>

            {/* Piece Completion Chart - 6 columns */}
            <Grid item xs={12} md={6}>
              <PieceCompletionChart data={reportData.pieceCompletion} />
            </Grid>
          </Grid>

          {/* Charts Grid Row 2 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Student Streak Chart - Full width */}
            <Grid item xs={12}>
              <StudentStreakChart data={reportData.streakTrends} />
            </Grid>
          </Grid>

          {/* Student Progress Table */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Student Details
                </Typography>
              </Box>
              <StudentProgressTable students={reportData.students} />
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
